import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  IconButton,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';

import useAuth from '../../auth/useAuth';
import useCart from '../../cart/useCart';
import { ROLES } from '../../config';
import ProductImagePlaceholder from '../../sections/client/products/ProductImagePlaceholder';
import QuantityStepper from '../../sections/client/products/QuantityStepper';

const formatMoney = (value, currency) =>
  new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-PE', { style: 'currency', currency }).format(value || 0);

export default function CartPage() {
  const { items, updateQuantity, removeItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedCurrency, setCurrency] = useState('PEN');

  // El distribuidor tiene su propio catalogo, distinto del catalogo publico, y solo compra en soles.
  const isDistributor = user?.role === ROLES.DISTRIBUIDOR;
  const catalogPath = isDistributor ? '/distribuidor' : '/catalogo';
  const currency = isDistributor ? 'PEN' : selectedCurrency;

  const priceField = currency === 'USD' ? 'price_usd' : 'price_pen';
  const total = items.reduce((sum, item) => sum + item.product[priceField] * item.quantity, 0);

  if (items.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <ShoppingCartOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
          <Typography variant="h5">Tu carrito está vacío</Typography>
          <Typography color="text.secondary">Agrega productos desde el catálogo para comenzar tu compra.</Typography>
          <Button variant="contained" component={RouterLink} to={catalogPath}>
            Ir al catálogo
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4">Carrito de compras</Typography>
        {!isDistributor && (
          <ToggleButtonGroup size="small" exclusive value={currency} onChange={(e, value) => value && setCurrency(value)}>
            <ToggleButton value="PEN">Soles</ToggleButton>
            <ToggleButton value="USD">Dólares</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      <Card>
        <CardContent>
          <Stack divider={<Divider />} spacing={2}>
            {items.map((item) => (
              <Stack sx={{ alignItems: 'center' }} key={item.product.id} direction="row" spacing={2}>
                <Box sx={{ width: 64, height: 64, flexShrink: 0, borderRadius: 1, overflow: 'hidden' }}>
                  {item.product.image_url ? (
                    <Box
                      component="img"
                      src={item.product.image_url}
                      alt={item.product.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', bgcolor: 'background.paper' }}
                    />
                  ) : (
                    <ProductImagePlaceholder height="100%" iconFontSize={24} />
                  )}
                </Box>
                <Stack sx={{ flexGrow: 1 }}>
                  <Typography variant="body1">{item.product.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatMoney(item.product[priceField], currency)} c/u
                  </Typography>
                </Stack>
                <QuantityStepper
                  value={item.quantity}
                  onChange={(quantity) => updateQuantity(item.product.id, quantity)}
                />
                <Typography sx={{ minWidth: 96, textAlign: 'right' }}>
                  {formatMoney(item.product[priceField] * item.quantity, currency)}
                </Typography>
                <IconButton onClick={() => removeItem(item.product.id)} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Total (IGV incluido)</Typography>
            <Typography variant="h6">{formatMoney(total, currency)}</Typography>
          </Stack>

          <Stack sx={{ justifyContent: 'space-between' }} direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Link component={RouterLink} to={catalogPath} sx={{ alignSelf: 'center' }}>
              Seguir comprando
            </Link>
            <Button variant="contained" size="large" onClick={() => navigate('/checkout')}>
              Continuar la compra
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}
